import { useEffect, useMemo, useState } from "react";
import { ErpShell } from "../components/layout/ErpShell.js";
import { FILTER_INPUT, FilterField, FilterPanel } from "../components/common/FilterBar.js";
import { useModals } from "../components/common/ModalsProvider.js";
import { useAuth } from "../hooks/useAuth.js";
import { useCanEdit } from "../hooks/useCanEdit.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { trpc } from "../lib/trpc.js";
import { useT } from "../i18n/index.js";
import { Paginacao } from "../components/common/Paginacao.js";

const PAGE_SIZE = 50;

// Termos da nomenclatura contábil francesa oficial (SIG / charges externes do
// PRD). Mantidos em FR pois aparecem nos relatórios fiscais para o cabinet
// Cockpit. `autres` é a única exceção genérica.
const CATEGORY_LABELS: Record<string, string> = {
  achats_consommes: "Achats consommés",
  fournitures_consommables: "Fournitures & consommables",
  locations_charges_locatives: "Locations & charges locatives",
  primes_assurance: "Primes d'assurance",
  intermediaires_honoraires: "Intermédiaires & honoraires",
  deplacements_reception: "Déplacements & réception",
  frais_bancaires: "Frais bancaires",
  impots_taxes: "Impôts & taxes",
  charges_personnel: "Charges de personnel",
  autres_charges_exploitation: "Autres charges d'exploitation",
  autres: "Outros",
};

interface SupplierForm {
  kind: "individual" | "business";
  displayName: string;
  legalName: string;
  siren: string;
  siret: string;
  vatNumber: string;
  countryCode: string;
  email: string;
  phone: string;
  defaultTvaRate: string;
  defaultCategory: string;
  notes: string;
}

const emptyForm: SupplierForm = {
  kind: "business",
  displayName: "",
  legalName: "",
  siren: "",
  siret: "",
  vatNumber: "",
  countryCode: "FR",
  email: "",
  phone: "",
  defaultTvaRate: "20",
  defaultCategory: "autres",
  notes: "",
};

export default function Suppliers() {
  const { user } = useAuth();
  const canEdit = useCanEdit();
  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );

  const [companyId, setCompanyId] = useSelectedCompany(accessibleCompanies);
  const [q, setQ] = useState("");
  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "inactive">("active");
  const [offset, setOffset] = useState(0);

  const filters = useMemo(
    () => ({
      companyId: companyId ?? 0,
      q: q || undefined,
      active: activeFilter === "all" ? undefined : activeFilter === "active",
      limit: PAGE_SIZE,
      offset,
    }),
    [companyId, q, activeFilter, offset],
  );

  const listQuery = trpc.suppliers.list.useQuery(filters, {
    enabled: companyId !== null,
  });
  const utils = trpc.useUtils();

  const [modal, setModal] = useState<
    { mode: "create" } | { mode: "edit"; supplierId: number } | null
  >(null);
  const t = useT();

  if (!user) return null;

  const isAdmin =
    user.isSuperAdmin ||
    user.memberships.some((m) => m.companyId === companyId && m.active && m.role === "admin");

  if (accessibleCompanies.length === 0) {
    return (
      <Shell>
        <p className="text-sm text-neutral-400">{t("suppliers.noCompanyAccess")}</p>
      </Shell>
    );
  }

  const data = listQuery.data;
  const total = data?.total ?? 0;

  return (
    <Shell>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <FilterField label={t("suppliers.companyLabel")}>
          <select
            value={companyId ?? ""}
            onChange={(e) => {
              setCompanyId(Number(e.target.value));
              setOffset(0);
            }}
            className={inputClass + " w-64"}
          >
            {accessibleCompanies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </FilterField>
        {canEdit && (
          <button
            onClick={() => setModal({ mode: "create" })}
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime px-4 py-1.5 text-sm font-medium"
          >
            + {t("suppliers.newSupplier")}
          </button>
        )}
      </div>

      <FilterPanel testId="suppliers-filter-panel">
        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          <FilterField label={t("suppliers.searchLabel")} className="grow max-w-xs">
            <input
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setOffset(0);
              }}
              placeholder={t("suppliers.searchPlaceholder")}
              className={FILTER_INPUT + " w-full"}
            />
          </FilterField>
          <FilterField label={t("suppliers.colStatus")}>
            <select
              value={activeFilter}
              onChange={(e) => {
                setActiveFilter(e.target.value as typeof activeFilter);
                setOffset(0);
              }}
              className={FILTER_INPUT + " w-full sm:w-40"}
            >
              <option value="active">{t("suppliers.filterActive")}</option>
              <option value="inactive">{t("suppliers.filterInactive")}</option>
              <option value="all">{t("suppliers.filterAll")}</option>
            </select>
          </FilterField>
        </div>
      </FilterPanel>

      <div className="rounded-lg border border-neutral-800 bg-neutral-900 overflow-hidden">
        {listQuery.isLoading && (
          <p className="p-4 text-sm text-neutral-500">{t("suppliers.loading")}</p>
        )}
        {listQuery.error && <p className="p-4 text-sm text-red-400">{listQuery.error.message}</p>}
        {data && data.rows.length === 0 && (
          <p className="p-4 text-sm text-neutral-500">{t("suppliers.emptyState")}</p>
        )}
        {data && data.rows.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-3 py-2">{t("suppliers.colName")}</th>
                <th className="text-left px-3 py-2 w-32">SIRET</th>
                <th className="text-left px-3 py-2 w-32">TVA</th>
                <th className="text-left px-3 py-2 w-48">{t("suppliers.colDefaultCategory")}</th>
                <th className="text-right px-3 py-2 w-20">TVA %</th>
                <th className="text-left px-3 py-2 w-20">{t("suppliers.colStatus")}</th>
                <th className="px-3 py-2 w-20 text-right">{t("suppliers.colActions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {data.rows.map((s) => (
                <tr key={s.id} className={s.active ? "" : "opacity-60"}>
                  <td className="px-3 py-2">
                    {s.displayName}
                    {s.legalName && s.legalName !== s.displayName && (
                      <div className="text-xs text-neutral-600">{s.legalName}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs font-mono text-neutral-400">
                    {s.siret ?? <span className="text-neutral-700">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs font-mono text-neutral-400">
                    {s.vatNumber ?? <span className="text-neutral-700">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-neutral-400">
                    {s.defaultCategory
                      ? (CATEGORY_LABELS[s.defaultCategory] ?? s.defaultCategory)
                      : "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs">
                    {s.defaultTvaRate ?? "—"}
                  </td>
                  <td className="px-3 py-2">
                    <ActiveBadge active={s.active} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    {canEdit && (
                      <button
                        onClick={() => setModal({ mode: "edit", supplierId: s.id })}
                        className="text-xs text-lime hover:underline"
                      >
                        {t("suppliers.editAction")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > 0 && (
        <Paginacao
          total={total}
          pageSize={PAGE_SIZE}
          offset={offset}
          onOffset={setOffset}
          noun={total === 1 ? t("suppliers.supplierSingular") : t("suppliers.supplierPlural")}
          testId="suppliers-pagination"
        />
      )}

      {modal && companyId !== null && (
        <SupplierModal
          companyId={companyId}
          mode={modal.mode}
          supplierId={modal.mode === "edit" ? modal.supplierId : undefined}
          isAdmin={!!isAdmin}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            utils.suppliers.invalidate();
          }}
        />
      )}
    </Shell>
  );
}

function SupplierModal({
  companyId,
  mode,
  supplierId,
  isAdmin,
  onClose,
  onSaved,
}: {
  companyId: number;
  mode: "create" | "edit";
  supplierId?: number;
  isAdmin: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const modals = useModals();
  const t = useT();
  const detailQuery = trpc.suppliers.getById.useQuery(
    { companyId, supplierId: supplierId ?? 0 },
    {
      enabled: mode === "edit" && !!supplierId,
      retry: false,
      staleTime: 0,
      refetchOnMount: "always",
    },
  );

  const [form, setForm] = useState<SupplierForm>(emptyForm);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== "edit") return;
    const d = detailQuery.data;
    if (!d) return;
    setForm({
      kind: d.kind,
      displayName: d.displayName,
      legalName: d.legalName ?? "",
      siren: d.siren ?? "",
      siret: d.siret ?? "",
      vatNumber: d.vatNumber ?? "",
      countryCode: d.countryCode,
      email: d.email ?? "",
      phone: d.phone ?? "",
      defaultTvaRate: d.defaultTvaRate ?? "",
      defaultCategory: d.defaultCategory ?? "autres",
      notes: d.notes ?? "",
    });
  }, [mode, detailQuery.data?.id, detailQuery.dataUpdatedAt]);

  const createMutation = trpc.suppliers.create.useMutation({
    onSuccess: onSaved,
    onError: (e) => setError(e.message),
  });
  const updateMutation = trpc.suppliers.update.useMutation({
    onSuccess: onSaved,
    onError: (e) => setError(e.message),
  });
  const deactivateMutation = trpc.suppliers.deactivate.useMutation({
    onSuccess: onSaved,
    onError: (e) => setError(e.message),
  });
  const reactivateMutation = trpc.suppliers.reactivate.useMutation({
    onSuccess: onSaved,
    onError: (e) => setError(e.message),
  });
  const pending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deactivateMutation.isPending ||
    reactivateMutation.isPending;

  function buildData() {
    const tvaRate = form.defaultTvaRate.trim()
      ? Number(form.defaultTvaRate.replace(",", "."))
      : undefined;
    return {
      kind: form.kind,
      displayName: form.displayName.trim(),
      legalName: form.legalName.trim() || undefined,
      siren: form.siren.trim() || undefined,
      siret: form.siret.trim() || undefined,
      vatNumber: form.vatNumber.trim() || undefined,
      countryCode: form.countryCode.trim().toUpperCase(),
      email: form.email.trim() || undefined,
      phone: form.phone.trim() || undefined,
      defaultTvaRate: tvaRate !== undefined && Number.isFinite(tvaRate) ? tvaRate : undefined,
      defaultCategory: form.defaultCategory as
        | "autres"
        | "achats_consommes"
        | "fournitures_consommables"
        | "locations_charges_locatives"
        | "primes_assurance"
        | "intermediaires_honoraires"
        | "deplacements_reception"
        | "frais_bancaires"
        | "impots_taxes"
        | "charges_personnel"
        | "autres_charges_exploitation",
      notes: form.notes.trim() || undefined,
    };
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (mode === "create") {
      createMutation.mutate({ companyId, data: buildData() });
    } else if (supplierId) {
      updateMutation.mutate({
        companyId,
        supplierId,
        patch: buildData(),
      });
    }
  }

  return (
    <ModalShell
      title={mode === "create" ? t("suppliers.newSupplier") : t("suppliers.editSupplier")}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="p-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("suppliers.fieldKind")}>
            <select
              value={form.kind}
              onChange={(e) =>
                setForm({ ...form, kind: e.target.value as "individual" | "business" })
              }
              disabled={pending}
              className={inputClass}
            >
              <option value="business">{t("suppliers.kindBusiness")}</option>
              <option value="individual">{t("suppliers.kindIndividual")}</option>
            </select>
          </Field>
          <Field label={t("suppliers.fieldCountry")} required>
            <input
              value={form.countryCode}
              onChange={(e) =>
                setForm({ ...form, countryCode: e.target.value.toUpperCase().slice(0, 2) })
              }
              maxLength={2}
              required
              disabled={pending}
              className={inputClass}
            />
          </Field>
          <Field label={t("suppliers.fieldDisplayName")} required>
            <input
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              required
              maxLength={200}
              disabled={pending}
              className={inputClass}
            />
          </Field>
          <Field label={t("suppliers.fieldLegalName")}>
            <input
              value={form.legalName}
              onChange={(e) => setForm({ ...form, legalName: e.target.value })}
              maxLength={255}
              disabled={pending}
              className={inputClass}
            />
          </Field>
          <Field label={t("suppliers.fieldSiren")}>
            <input
              value={form.siren}
              onChange={(e) =>
                setForm({ ...form, siren: e.target.value.replace(/\D/g, "").slice(0, 9) })
              }
              maxLength={9}
              disabled={pending}
              placeholder="933207862"
              className={`${inputClass} font-mono`}
            />
          </Field>
          <Field label={t("suppliers.fieldSiret")}>
            <input
              value={form.siret}
              onChange={(e) =>
                setForm({ ...form, siret: e.target.value.replace(/\D/g, "").slice(0, 14) })
              }
              maxLength={14}
              disabled={pending}
              placeholder="93320786200014"
              className={`${inputClass} font-mono`}
            />
          </Field>
          <Field label={t("suppliers.fieldVatNumber")}>
            <input
              value={form.vatNumber}
              onChange={(e) => setForm({ ...form, vatNumber: e.target.value.toUpperCase() })}
              maxLength={50}
              disabled={pending}
              placeholder="FR25933207862"
              className={`${inputClass} font-mono`}
            />
          </Field>
          <Field label={t("suppliers.fieldEmail")}>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              maxLength={320}
              disabled={pending}
              className={inputClass}
            />
          </Field>
          <Field label={t("suppliers.fieldPhone")}>
            <input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              maxLength={50}
              disabled={pending}
              className={inputClass}
            />
          </Field>
          <Field label={t("suppliers.fieldDefaultTvaRate")}>
            <input
              type="text"
              inputMode="decimal"
              value={form.defaultTvaRate}
              onChange={(e) => setForm({ ...form, defaultTvaRate: e.target.value })}
              disabled={pending}
              placeholder="20"
              className={`${inputClass} font-mono`}
            />
          </Field>
          <Field label={t("suppliers.fieldDefaultCategory")}>
            <select
              value={form.defaultCategory}
              onChange={(e) => setForm({ ...form, defaultCategory: e.target.value })}
              disabled={pending}
              className={inputClass}
            >
              {Object.entries(CATEGORY_LABELS).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label={t("suppliers.fieldNotes")}>
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            maxLength={4000}
            rows={2}
            disabled={pending}
            className={`${inputClass} resize-y`}
          />
        </Field>

        {error && (
          <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-neutral-800">
          <div className="flex gap-2">
            {mode === "edit" && supplierId && detailQuery.data && isAdmin && (
              <>
                {detailQuery.data.active ? (
                  <button
                    type="button"
                    onClick={async () => {
                      const reason = await modals.promptReason({
                        title: `${t("suppliers.deactivateTitlePrefix")} "${detailQuery.data.displayName}"`,
                        message: t("suppliers.deactivateMessage"),
                        placeholder: t("suppliers.deactivatePlaceholder"),
                        confirmLabel: t("suppliers.deactivate"),
                        tone: "danger",
                        required: true,
                      });
                      if (!reason) return;
                      deactivateMutation.mutate({ companyId, supplierId, reason });
                    }}
                    disabled={pending}
                    className="rounded-md border border-red-500/40 hover:border-red-500 text-red-400 px-3 py-1.5 text-xs"
                  >
                    {t("suppliers.deactivate")}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => reactivateMutation.mutate({ companyId, supplierId })}
                    disabled={pending}
                    className="rounded-md border border-lime/40 hover:border-lime text-lime px-3 py-1.5 text-xs"
                  >
                    {t("suppliers.reactivate")}
                  </button>
                )}
              </>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={pending}
              className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
            >
              {t("suppliers.cancel")}
            </button>
            <button
              type="submit"
              disabled={pending}
              className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium"
            >
              {pending
                ? t("suppliers.saving")
                : mode === "create"
                  ? t("suppliers.create")
                  : t("suppliers.save")}
            </button>
          </div>
        </div>
      </form>
    </ModalShell>
  );
}

const inputClass =
  "w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none disabled:opacity-50";

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs text-neutral-400 mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {children}
    </div>
  );
}

function ActiveBadge({ active }: { active: boolean }) {
  const t = useT();
  return active ? (
    <span className="inline-block rounded bg-lime/20 text-lime px-2 py-0.5 text-xs">
      {t("suppliers.badgeActive")}
    </span>
  ) : (
    <span className="inline-block rounded bg-neutral-700/40 text-neutral-400 px-2 py-0.5 text-xs">
      {t("suppliers.badgeInactive")}
    </span>
  );
}

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-2xl bg-neutral-950 border border-neutral-800 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-neutral-800 flex items-center justify-between sticky top-0 bg-neutral-950">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-300 text-lg leading-none"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <ErpShell>
      <main className="max-w-6xl mx-auto p-6 space-y-4">{children}</main>
    </ErpShell>
  );
}
